import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { reportStrings, commonStrings } from '@/constants/residents/marketplaceStrings';
import { INPUT_LIMITS } from '@/constants/residents/marketplaceConstants';
import { ReportReason, REPORT_REASONS } from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { BaseModal, RadioButton } from './shared';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface ReportModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason, description?: string) => void;
  hasAlreadyReported?: boolean;
  isSubmitting?: boolean;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  visible,
  onClose,
  onSubmit,
  hasAlreadyReported = false,
  isSubmitting = false,
}) => {
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (selectedReason && !isSubmitting) {
      onSubmit(selectedReason, description.trim() || undefined);
      // Reset form
      setSelectedReason(null);
      setDescription('');
    }
  };

  const handleClose = () => {
    // Reset form on close
    setSelectedReason(null);
    setDescription('');
    onClose();
  };

  // Already reported state
  if (hasAlreadyReported) {
    const footer = (
      <TouchableOpacity
        style={styles.submitButton}
        onPress={handleClose}
        activeOpacity={0.8}
      >
        <Text style={styles.submitButtonText}>{commonStrings.close}</Text>
      </TouchableOpacity>
    );

    return (
      <BaseModal
        visible={visible}
        onClose={handleClose}
        showHeader={true}
        headerStyle={{ borderBottomWidth: 0 }}
        footer={footer}
      >
        <View style={styles.alreadyReportedContent}>
          {/* Success Icon */}
          <View style={styles.successIconContainer}>
            <View style={styles.successIconOuter}>
              <View style={styles.successIconInner}>
                <CheckIcon width={32} height={32} color={MARKETPLACE_COLORS.textPrimary} />
              </View>
            </View>
          </View>

          <Text style={styles.alreadyReportedTitle}>{reportStrings.reportSubmitted}</Text>
          <Text style={styles.alreadyReportedDescription}>
            {reportStrings.alreadyReported}
          </Text>
        </View>
      </BaseModal>
    );
  }

  // Report form state
  const isDisabled = !selectedReason || isSubmitting;
  const footer = (
    <TouchableOpacity
      style={[
        styles.submitButton,
        isDisabled && styles.submitButtonDisabled,
      ]}
      onPress={handleSubmit}
      activeOpacity={0.8}
      disabled={isDisabled}
    >
      {isSubmitting ? (
        <ActivityIndicator size="small" color={MARKETPLACE_COLORS.background} />
      ) : (
        <Text style={[
          styles.submitButtonText,
          isDisabled && styles.submitButtonTextDisabled,
        ]}>
          {commonStrings.submit}
        </Text>
      )}
    </TouchableOpacity>
  );

  return (
    <BaseModal
      visible={visible}
      onClose={handleClose}
      showHeader={true}
      headerStyle={{ borderBottomWidth: 0 }}
      footer={footer}
      scrollable={true}
      avoidKeyboard={true}
    >
      {/* Title and Description */}
      <View style={styles.titleSection}>
        <Text style={styles.title}>{reportStrings.title}</Text>
        <Text style={styles.subtitle}>
          {reportStrings.subtitle}
        </Text>
      </View>

      {/* Report Options */}
      <View style={styles.optionsList}>
        {REPORT_REASONS.map((reason) => (
          <TouchableOpacity
            key={reason.value}
            style={styles.optionItem}
            onPress={() => setSelectedReason(reason.value)}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.optionLabel,
              selectedReason === reason.value && styles.optionLabelSelected,
            ]}>
              {reason.label}
            </Text>
            <RadioButton isSelected={selectedReason === reason.value} />
          </TouchableOpacity>
        ))}

        {/* Description Input */}
        <View style={styles.descriptionContainer}>
          <TextInput
            style={styles.descriptionInput}
            placeholder={reportStrings.descriptionOptional}
            placeholderTextColor={MARKETPLACE_COLORS.textSecondary}
            value={description}
            onChangeText={setDescription}
            multiline
            textAlignVertical="top"
            maxLength={INPUT_LIMITS.reportDescription}
          />
        </View>
      </View>
    </BaseModal>
  );
};

const styles = StyleSheet.create({
  titleSection: {
    alignItems: 'center',
    gap: 6,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 36,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 24,
    textAlign: 'center',
  },
  optionsList: {
    gap: 16,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
    flex: 1,
  },
  optionLabelSelected: {
    fontWeight: '500',
  },
  descriptionContainer: {
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    height: 80,
  },
  descriptionInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 11,
    paddingBottom: 11,
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 17.5,
  },
  submitButton: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: MARKETPLACE_COLORS.buttonDisabled,
  },
  submitButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.background,
    lineHeight: 18,
  },
  submitButtonTextDisabled: {
    color: MARKETPLACE_COLORS.buttonDisabledText,
  },
  // Already Reported State
  alreadyReportedContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 24,
  },
  successIconContainer: {
    marginBottom: 10,
  },
  successIconOuter: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: MARKETPLACE_COLORS.accentBlueLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIconInner: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
    alignItems: 'center',
    justifyContent: 'center',
  },
  alreadyReportedTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 36,
    textAlign: 'center',
  },
  alreadyReportedDescription: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 24,
    textAlign: 'center',
  },
});
